/**
 * Backend (consultorias) no modo ao vivo: sem negócio simulado. Três coisas com nomes diferentes
 * (achado 72; ver `realBackendTypes.ts`):
 *
 * - Contratos de consultoria (R$ 100 mil): o registro ainda não existe. Placar, quadro, valor em jogo,
 *   etapas B0 em diante, conversas do WhatsApp e "em conversa" ficam sem número (nada de zero com cara
 *   de medido); o comando `backend` recusa com "Registro de consultorias ainda não disponível", para
 *   nada ir para a memória da simulação.
 * - Curso e 20X: os pedidos pagos da Kiwify do período e dos Filtros (a mesma regra das Vendas), no
 *   funil, na origem e na segunda chance; o placar conta os compradores do 20X desde o início.
 * - Consulta individual (Premium, US$ 397): os pedidos pagos do Programa América Premium, no período e
 *   no mês (São Paulo). A meta de consultas do mês só aparece se o dono a salvou em Ajustes.
 */
import type { BackendConsultingPace, BackendReport, ProviderError, Query, Settings } from '@rota/contracts';
import { getWithRetry } from './http';
import {
  BACKEND_REGISTRY_OFF,
  type CountCents,
  type LiveBackendExtras,
  type LiveBackendReport,
} from './realBackendTypes';
import {
  channelV2,
  DAY,
  dayStart,
  isPaid,
  matches,
  type RealCtx,
  type RealModule,
  type RealOrderRow,
  rangeMs,
  rate,
  revenueOf,
  todayKey,
} from './realCore';

/** KPIs da V2 que só existem com consultorias registradas: fora até o backend real existir. */
export const BACKEND_KPIS = [
  'buyerValueBackend',
  'roasFunnelBackend',
  'backendPerFrontBuyer',
  'backendOpen',
  'backendValueAtStake',
  'backendCloseRate',
] as const;

/** Consulta individual (Premium) paga: pedidos Premium aprovados (valor do produto, sem juros). */
export function paidConsultings(rows: readonly RealOrderRow[]): CountCents {
  const paid = rows.filter((o) => o.kind === 'premium' && isPaid(o));
  return { count: paid.length, cents: paid.reduce((s, o) => s + revenueOf(o), 0) };
}

/** Início do mês corrente em São Paulo. */
const monthStart = (now: number): number => dayStart(`${todayKey(now).slice(0, 7)}-01`);

/** Desde o início: a loja real começou em 2026 (antes disso não há pedido na base). */
const SINCE_START = dayStart('2026-01-01');

/** O servidor aceita no máximo 120 dias por pedido de vendas (`MAX_RANGE_MS` em apps/server). */
const MAX_WINDOW = 120 * DAY;

/**
 * Janelas de até 120 dias cobrindo [from, to), sem buraco nem sobreposição. "Desde o início" num
 * pedido só (1º jan até hoje, mais de 120 dias) voltava 400 e derrubava o Backend inteiro e a
 * "Suas consultorias" da Pauta (30/10/2026).
 */
export function rangeWindows(from: number, to: number, max = MAX_WINDOW): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let f = from; f < to; f += max) out.push([f, Math.min(f + max, to)]);
  return out;
}

/** Pedidos de [from, to) em janelas de até 120 dias, sem repetir pedido que caia na borda. */
async function loadWindows(
  load: (from: number, to: number) => Promise<RealOrderRow[]>,
  from: number,
  to: number,
): Promise<RealOrderRow[]> {
  const parts = await Promise.all(rangeWindows(from, to).map(([f, t]) => load(f, t)));
  const seen = new Set<string>();
  return parts.flat().filter((o) => {
    if (seen.has(o.id)) return false;
    seen.add(o.id);
    return true;
  });
}

/** Nome do canal na Origem (o mesmo da suíte: "Anúncios", "Orgânico · bio"…). */
const ORIGIN_LABEL: Record<(typeof channelV2)[keyof typeof channelV2], string> = {
  meta_ads: 'Anúncios',
  ig_bio: 'Orgânico · bio',
  ig_dm: 'Orgânico · direct',
  ig_outro: 'Orgânico · outro',
  sem_origem: 'Sem origem',
};

/** Quem comprou (pedaço do hash do e-mail); sem ele, o próprio pedido conta como uma pessoa. */
const buyerOf = (o: RealOrderRow): string => o.buyer ?? `pedido:${o.id}`;

/** Curso e 20X pagos no recorte (a mesma regra do KPI de vendas: pago e dentro dos Filtros). */
export function frontUpsell(rows: readonly RealOrderRow[], q: Query) {
  const paid = rows.filter((o) => isPaid(o) && matches(o, q));
  const front = paid.filter((o) => o.kind === 'front');
  const upsell = paid.filter((o) => o.kind === 'upsell');
  return { front, upsell };
}

/** Origem por canal: compradores do curso e do 20X (propostas, fechadas e R$ não são registrados). */
export function originRows(front: readonly RealOrderRow[], upsell: readonly RealOrderRow[]) {
  const rows = new Map<string, BackendReport['byOrigin'][number]>();
  const add = (o: RealOrderRow, k: 'frontBuyers' | 'upsellBuyers') => {
    const label = ORIGIN_LABEL[channelV2[o.channel]];
    const r = rows.get(label) ?? { label, frontBuyers: 0, upsellBuyers: 0, proposals: 0, won: 0, cents: 0 };
    r[k] += 1;
    rows.set(label, r);
  };
  for (const o of front) add(o, 'frontBuyers');
  for (const o of upsell) add(o, 'upsellBuyers');
  // Mais compradores primeiro; "Sem origem" sempre por último (nunca presumida orgânica nem paga).
  return [...rows.values()].sort(
    (a, b) =>
      Number(a.label === ORIGIN_LABEL.sem_origem) - Number(b.label === ORIGIN_LABEL.sem_origem) ||
      b.frontBuyers - a.frontBuyers ||
      a.label.localeCompare(b.label, 'pt-BR'),
  );
}

/** Meta de consultas salva pelo dono em Ajustes (sem ela, null: nada de 3/dia e 90/mês inventados). */
export function savedGoal(s: Settings | null): { perDay: number; perMonth: number } | null {
  const c = (s as { consultingCapacity?: { perDay?: number; perMonth?: number } } | null)?.consultingCapacity;
  if (!c || typeof c.perDay !== 'number' || typeof c.perMonth !== 'number') return null;
  if (!(c.perDay > 0) || !(c.perMonth > 0)) return null;
  return { perDay: c.perDay, perMonth: c.perMonth };
}

/**
 * Consultas do mês × meta salva (o mesmo critério da meta da simulação): ritmo pelos últimos 7 dias,
 * projeção até o fim do mês e "acima" quando passa da meta (sugere preparar o time, nunca teto).
 */
export function consultingPace(
  goal: { perDay: number; perMonth: number },
  monthToDate: number,
  last7d: number,
  now: number,
): BackendConsultingPace {
  const key = todayKey(now);
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const dayFrac = d - 1 + (now - dayStart(key)) / DAY;
  const perDay = Math.max(0, last7d / 7);
  const projectedMonth = monthToDate + perDay * Math.max(0, daysInMonth - dayFrac);
  const share = Math.max(perDay / goal.perDay, projectedMonth / goal.perMonth);
  return {
    perDayGoal: goal.perDay,
    monthGoal: goal.perMonth,
    monthToDate,
    expectedToDate: Math.min(goal.perMonth, goal.perDay * d),
    perDay,
    perDaySource: 'last7d',
    last7d,
    projectedMonth,
    dayOfMonth: d,
    daysInMonth,
    status:
      perDay > goal.perDay || projectedMonth > goal.perMonth ? 'above' : share >= 0.9 ? 'on_track' : 'below',
  };
}

/** Recusa do comando no modo ao vivo (o quadro mostra o erro; nada vai para a simulação). */
const registryOff = (): ProviderError => ({ code: 'source_unavailable', message: BACKEND_REGISTRY_OFF });

/** Leads para consultoria esperando no direct agora (`GET /api/painel/fila`); null = sem resposta. */
async function fetchConsultingLeads(): Promise<number | null> {
  try {
    const r = await getWithRetry('/api/painel/fila');
    if (!r.ok) return null;
    const j = (await r.json()) as { consultoria?: { waiting?: unknown } };
    const n = j.consultoria?.waiting;
    return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}

export function backendModule(
  ctx: RealCtx,
  loadLeads: () => Promise<number | null> = fetchConsultingLeads,
): RealModule {
  const { v2 } = ctx;

  async function getBackend(q: Query): Promise<LiveBackendReport> {
    const now = ctx.clock();
    const { from, to } = rangeMs(q, now);
    const mStart = monthStart(now);
    const nowMin = Math.ceil(now / 60_000) * 60_000;
    const [inPeriod, inMonth, allTime, last7, leads] = await Promise.all([
      ctx.load(from, to),
      ctx.load(mStart, Math.max(nowMin, mStart + 1)),
      loadWindows(ctx.load, SINCE_START, nowMin),
      ctx.load(nowMin - 7 * DAY, nowMin),
      loadLeads(),
    ]);
    const { front, upsell } = frontUpsell(inPeriod, q);
    const frontBuyers = new Set(front.map(buyerOf));
    const upsellBuyers = new Set(upsell.map(buyerOf));
    const withoutUpsell = [...frontBuyers].filter((b) => !upsellBuyers.has(b)).length;
    const period = paidConsultings(inPeriod.filter((o) => matches(o, q)));
    // O mês da consulta Premium não segue o período nem os Filtros (é o número do mês, como na TV).
    const month = paidConsultings(inMonth);
    const goal = savedGoal(ctx.settings());
    const extras: LiveBackendExtras = {
      live: true,
      registry: 'off',
      upsellAllTime: allTime.filter((o) => isPaid(o) && o.kind === 'upsell').length,
      upsellInPeriod: upsell.length,
      premium: { period, month },
      goalSaved: goal != null,
      consultingLeads: leads,
    };
    return {
      // Contratos de consultoria: sem registro, nenhum aberto, contratado ou recebido.
      open: 0,
      valueAtStake: 0,
      ceilingAtStake: 0,
      closedCount: 0,
      closedCents: 0,
      receivedCents: 0,
      closeRateOnProposals: null,
      medianCycleDays: null,
      overdue: 0,
      // Só os degraus medidos: curso e 20X pagos. B0 em diante ainda não são registrados.
      funnel: [
        { stage: 'front', count: front.length, rateFromPrev: null, ci90: null },
        {
          stage: 'upsell',
          count: upsell.length,
          rateFromPrev: rate(upsell.length, front.length),
          ci90: null,
        },
      ],
      byOrigin: originRows(front, upsell),
      deals: [],
      sampleNote: 'o registro dos contratos de consultoria ainda não está ligado',
      monthToDate: month,
      ...(goal
        ? {
            consulting: consultingPace(goal, month.count, paidConsultings(last7).count, now),
          }
        : {}),
      secondChance: {
        frontBuyers: frontBuyers.size,
        upsellBuyers: upsellBuyers.size,
        withoutSecondOffer: withoutUpsell,
        // "Em conversa com o Tiago" não é registrado: a tela mostra "—" (ver `registry`).
        inConversation: 0,
        conversationRate: null,
        goal: 0.1,
        invitedRate: null,
        attendedRate: null,
        upsellFromClassRate: null,
      },
      ...extras,
    };
  }

  /** Nenhum comando do backend vai para a simulação: o registro real ainda não existe. */
  async function backend(): Promise<never> {
    throw registryOff();
  }

  return {
    overrides: v2.getBackend ? { getBackend, backend } : { backend },
    patchSnapshot(_q, snap) {
      if (snap.kpisV2) for (const id of BACKEND_KPIS) delete snap.kpisV2[id];
      // Contratos de consultoria: nenhum registrado de verdade ainda (o resumo simulado sai).
      if (snap.backend) snap.backend = { open: 0, valueAtStake: 0, nextDueAt: null };
    },
    patchKpisV2(_q, out) {
      for (const id of BACKEND_KPIS) delete out[id];
    },
  };
}
